import { getBackendUrl } from "@Utils";
import ExpandAll from "../../../assets/images/Expand All.svg?react";
import CollapseAll from "../../../assets/images/Collapse All.svg?react";
import ZoomIn from "../../../assets/images/Zoom In.svg?react";
import ZoomOut from "../../../assets/images/Zoom Out.svg?react";
import ZoomToFit from "../../../assets/images/Zoom To Fit.svg?react";
import FormatIndentIncreaseIcon from "@mui/icons-material/FormatIndentIncrease";
import FormatIndentDecreaseIcon from "@mui/icons-material/FormatIndentDecrease";
import ExportIcon from "@Assets/icons/svg/Export.svg?react";
import ScheduleIcon from "@Assets/icons/svg/Schedule_Now.svg?react";
import UpdateIcon from "@Assets/icons/svg/Update.svg?react";
import CancelIcon from "@Assets/icons/svg/Cancel.svg?react";
import ProgressUpdates from "../../../assets/icons/svg/ProgressUpdates.svg?react";
import EditIcon from "@Assets/icons/edit.svg?react";
import DeleteIcon from "@Assets/icons/svg/delete.svg?react";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { useRef, useState } from "react";
import { getContractCRUDPermission } from "@Features/commercial-shared/contracts/contractHelpers";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation, untranslated } from "@i18n";
import IconButton from "@mui/material/IconButton";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import ActivityIcon from "@Assets/icons/svg/ActivityIcon.svg?react";
import ImportIcon from "@Assets/icons/svg/Import.svg?react";
import DownArrow from "@Assets/icons/svg/BottomArrow.svg?react";
import P6Icon from "@Assets/icons/svg/P6Logo.svg?react";
import MppIcon from "@Assets/icons/svg/MPP.svg?react";
import XlsxIcon from "@Assets/icons/svg/XLSX.svg?react";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import Text from "@CubeComponents/html/Text/Text";
import { showToastNotification } from "../../../reducers/toastSlice";
import { setScheduleDetails } from "@Actions/task";
import {
  SOMETHING_WENT_WRONG,
  TOAST_TYPE_ERROR,
  TOAST_TYPE_INFO,
  TOAST_TYPE_SUCCESS,
} from "../../../services/constants";
import IconComponent from "@CubeComponents/icons";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { GanttStatic, Task } from "./dhtmlxgantt";
import { ToolbarProps } from "../interface";
import useGanttHelper from "./useGanttHelper";
import { importScheduleTasksNewParams } from "@ServerTypes/tasks/tasks.schema";
import type { GetScheduleActivitiesResponse } from "@ServerTypes/schedule/schedule.schema";
import type { ScheduleDetailRecord } from "@ServerTypes/tasks/tasks.schema";
import axiosWrapper from "../../../../src/services/axiosWrapper";
import { exportType, folderPermissions } from "@Helpers/constants";
import { uploadStringToTemp } from "@Services/tempUpload";
import { deleteScheduleActivityUrl } from "../../../services/dataManager";

import { convertDate } from "@Services/luxonHelper";
import MenuItemComponent from "@CubeComponents/html/MenuItemComponent";
import MenuComponent from "@CubeComponents/html/MenuComponent";
import { RootState } from "@Reducers/index";

/**
 * What the DHTMLX P6/MSP importers hand to their `callback`: the backend's import response, or
 * nothing when the import failed before reaching the server.
 */
type ImportProjectResult = { status?: boolean; worktimes?: ScheduleDetailRecord["worktimes"] } | undefined;

/** Axios rejection shape the export handler reads: only the server's `message` is consulted. */
type ApiErrorResponse = { response?: { data?: { message?: string } } };
declare const gantt: GanttStatic;

export type GanttUpdateItem = {
  id: string;
  progress?: number;
  plannedStartDate?: Date | null;
  plannedEndDate?: Date | null;
  plannedDuration?: number;
  actualStartDate?: Date | null;
  actualEndDate?: Date | null;
  type?: string;
};

export function applyParentTypeUpdates(
  newParent: string | number | null | undefined,
  oldParent: string | null | undefined,
  updates: GanttUpdateItem[],
): void {
  const newParentStr = newParent ? String(newParent) : null;

  if (newParentStr && newParentStr !== "0" && gantt.isTaskExists(newParentStr)) {
    gantt.getTask(newParentStr).type = "project";
    const existing = updates.find((u) => u.id === newParentStr);
    if (existing) existing.type = "project";
    else updates.push({ id: newParentStr, type: "project" });
  }
  if (oldParent && oldParent !== "0" && gantt.isTaskExists(oldParent) && !gantt.hasChild(oldParent)) {
    gantt.getTask(oldParent).type = "task";
    const existing = updates.find((u) => u.id === oldParent);
    if (existing) existing.type = "task";
    else updates.push({ id: oldParent, type: "task" });
  }
}

const useToolBarInGantt = (props: ToolbarProps) => {
  const { t } = useTranslation();
  const {
    contractId,
    scheduleId,
    projectId,
    spaceId,
    enableImportButton,
    currentSelectedTask,
    setShowActivityInformationModal,
    inlineEditingEnabled,
    contractDetails,
    changeOrderState,
    setShowProgressModal,
    setCurrentSelectedTask,
    setShowShimmer,
    setShowWarning,
    setShowBoqModal,
    attachTaskModalState,
    taskIdAndPredecessorMapper,
    ganttConfig,
    setRerenderGantt,
    showDeleteActivityModal,
    setShowDeleteActivityModal,
    scheduleVersionId,
    timezone,
    refreshParentSummaries,
    saveBatchSummaryUpdates,
    setSkipAfterTaskUpdate,
    isDeletingTask,
    isGrouped,
  } = props;

  const { getHoursPerDay, P6, MSP, SupportedExtension, XER, MPP } = useGanttHelper();

  const [newButtonAnchorEl, setNewButtonAnchorEl] = useState<HTMLElement | null>(null);
  const [exportButtonAnchorEl, setExportButtonAnchorEl] = useState<HTMLElement | null>(null);
  const [currentZoomLevel, setCurrentZoomLevel] = useState("min");
  const [showImportModal, setShowImportModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [importTasksInProgress, setImportTasksInProgress] = useState(false);
  const [updateTasksInProgress, setUpdateTasksInProgress] = useState(false);
  const [importBaseline, setImportBaseline] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [updateFile, setUpdateFile] = useState<File | null>(null);
  const [fileSoftware, setFileSoftware] = useState<string>("");
  const [baselineFile, setBaselineFile] = useState<File | null>(null);
  const newButtonOpen = Boolean(newButtonAnchorEl);
  const exportButtonOpen = Boolean(exportButtonAnchorEl);
  const server = `${getBackendUrl()}/schedule/exportScheduleTasks?scheduleId=${scheduleId}`;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const BaselineInputRef = useRef<HTMLInputElement | null>(null);
  const updateFileInputRef = useRef<HTMLInputElement | null>(null);
  const dispatch = useDispatch();
  const { GanttDetails } = useSelector((state: RootState) => state);
  const { RESOURCE_MODIFY, RESOURCE_DOWNLOAD } = folderPermissions();
  const userPermissions = GanttDetails?.userPermissions || [];
  const contractPermissions = contractDetails?.userPermissions || [];
  const canEdit = userPermissions.includes(RESOURCE_MODIFY.id);
  const canDownload = userPermissions.includes(RESOURCE_DOWNLOAD.id);
  const contractCanDownload = contractPermissions.includes(RESOURCE_DOWNLOAD.id);

  const zoomConfig = useRef({
    levels: [
      {
        // Month scale
        scale_unit: "month",
        step: 1,
        date_scale: "%F, %Y",
        subscales: [{ unit: "day", step: 1, date: "%d" }],
      },
      {
        // Year scale
        scale_unit: "year",
        step: 1,
        date_scale: "%Y",
        subscales: [{ unit: "month", step: 1, date: "%M" }],
      },
      {
        // Decade scale
        scale_unit: "year",
        step: 10, // Each step is 10 years
        date_scale: "%Y",
        subscales: [{ unit: "year", step: 1, date: "%Y" }], // Show individual years
      },
    ],
    currentLevel: 1, // Start with a moderate zoom level
  });
  const customImportFields = [
    "ID",
    "Baseline",
    "Milestone",
    "Summary",
    "calendar_id",
    "RemainingDuration",
    "Critical",
    "TotalSlack",
  ];
  type importParamsWithCallback = importScheduleTasksNewParams & {
    callback: (project: ImportProjectResult) => void;
  };

  const newButtonTemplate = () => (
    <>
      <ButtonComponent
        disabled={currentSelectedTask.id || !canEdit || isGrouped}
        id="addButton"
        className={`${currentSelectedTask.id || !canEdit || isGrouped ? "opacity-40 cursor-not-allowed grayscale select-none pointer-events-auto [&>*]:pointer-events-none" : ""} ms-6`}
        onClick={(event) => setNewButtonAnchorEl(event.currentTarget)}
      >
        <Text className="flex gap-2 items-center font-semibold">
          <AddIcon className="h-4 w-4" />
          {t("New")}
          <DownArrow />
        </Text>
      </ButtonComponent>
      <MenuComponent
        slotProps={{
          list: {
            "aria-labelledby": "addButton",
          },
        }}
        anchorEl={newButtonAnchorEl}
        open={newButtonOpen}
        onClose={() => setNewButtonAnchorEl(null)}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItemComponent
          onClick={() => {
            setShowActivityInformationModal(true);
            setNewButtonAnchorEl(null);
          }}
        >
          <ActivityIcon />
          <Text>{t("Activity")}</Text>
        </MenuItemComponent>
        <MenuItemComponent
          onClick={() => {
            setShowImportModal(true);
            setNewButtonAnchorEl(null);
          }}
          disabled={!enableImportButton}
        >
          <ImportIcon />
          <Text>{t("Import")}</Text>
        </MenuItemComponent>
        <MenuItemComponent
          onClick={() => {
            setShowUpdateModal(true);
            setNewButtonAnchorEl(null);
          }}
          disabled={enableImportButton}
        >
          <UpdateIcon className="h-4 w-4" />
          <Text>{t("Update Schedule")}</Text>
        </MenuItemComponent>
      </MenuComponent>
    </>
  );

  const getTasksForExport = () => {
    const allTasks = gantt.getTaskByTime();
    // Every task the chart holds is scheduled, so `duration`/`start_date`/`end_date` are set even
    // though DHTMLX declares them optional (they are only absent on `unscheduled: true` tasks).
    // Erased assertions.
    return allTasks.map((task: Task) => {
      const duration =
        ganttConfig.current === "hour" ? Math.ceil(task.duration! / getHoursPerDay(task)) : task.duration;

      const plannedDuration =
        ganttConfig.current === "hour" ? Math.ceil(task.plannedDuration / getHoursPerDay(task)) : task.plannedDuration;

      const TaskDisp = String(task.TaskDisp ?? "");
      task.convertdStartDate = convertDate(new Date(task.start_date!).getTime(), "dateForSchedule", timezone);
      task.convertdEndDate = convertDate(new Date(task.end_date!).getTime(), "dateForSchedule", timezone);
      task.convertedActualStartDate = convertDate(
        new Date(task.actualStartDate).getTime(),
        "dateForSchedule",
        timezone,
      );
      task.convertedActualEndDate = convertDate(new Date(task.actualEndDate).getTime(), "dateForSchedule", timezone);
      task.convertedPlannedStartDate = convertDate(
        new Date(task.plannedStartDate).getTime(),
        "dateForSchedule",
        timezone,
      );
      task.convertedPlannedEndDate = convertDate(new Date(task.plannedEndDate).getTime(), "dateForSchedule", timezone);

      return {
        ...task,
        TaskDisp,
        duration,
        plannedDurationExport: plannedDuration,
        progress: task.progress ? (task.progress * 100).toFixed(2) : 0,
        remDurationExport: task.remDuration,
      };
    });
  };

  const setupGanttExportForm = (type: string) => {
    const template = document.createElement("div");
    template.style.display = "none";
    template.innerHTML = `<form method='POST' id='ganttExportForm_${type}'><textarea id='ganttExportForm_data_${type}' name='data' style='width:0px; height:0px;' readonly='true'></textarea><input type='hidden' name='type' value=''></form>`;
    document.body.appendChild(template);
    gantt.ext.export_api._hidden_export_form = template;

    const form = document.getElementById(`ganttExportForm_${type}`) as HTMLFormElement;

    // DHTMLX's export API calls `form.submit()`; this replaces the DOM method with an async uploader.
    // `HTMLFormElement.submit` is declared `(): void`, which accepts any return value.
    form.submit = async function () {
      try {
        const data = (document.getElementById(`ganttExportForm_data_${type}`) as HTMLTextAreaElement).value;

        // Upload data directly to S3 from frontend
        const awsKey = await uploadStringToTemp(data, `schedule_export_${Date.now()}.json`);

        if (!awsKey) {
          dispatch(
            showToastNotification({
              type: TOAST_TYPE_ERROR,
              content: untranslated(SOMETHING_WENT_WRONG),
            }),
          );
          return false;
        }

        // Send only the S3 key to backend
        // SCHEMA GAP: /schedule/exportScheduleTasks has no declared response schema; these are the
        // two fields the handler really returns.
        const res = await axiosWrapper.post<{ status?: boolean; message?: string }>(server, {
          awsKey,
          type,
        });

        if (res.data.status === true) {
          dispatch(
            showToastNotification({
              type: TOAST_TYPE_INFO,
              content: untranslated(res.data.message!),
            }),
          );
        }
      } catch (err) {
        console.log(err);
        dispatch(
          showToastNotification({
            type: TOAST_TYPE_ERROR,
            content: untranslated((err as ApiErrorResponse).response?.data?.message || SOMETHING_WENT_WRONG),
          }),
        );
      } finally {
        template.remove();
        gantt.ext.export_api._hidden_export_form = null;
      }
      return false;
    };
  };

  const handleExcelExport = () => {
    const tasksForExport = getTasksForExport();
    setupGanttExportForm(exportType.EXCEL);

    gantt.exportToExcel({
      server,
      data: tasksForExport,
      columns: [
        { id: "TaskDisp", header: t("ID"), type: "string" },
        { id: "text", header: t("Activity"), type: "string" },
        { id: "convertdStartDate", header: t("Start Date"), type: "string" },
        { id: "convertdEndDate", header: t("End Date"), type: "string" },
        { id: "duration", header: t("Duration"), type: "number" },
        { id: "convertedActualStartDate", header: t("Actual Start Date"), type: "string" },
        { id: "convertedActualEndDate", header: t("Actual End Date"), type: "string" },
        { id: "convertedPlannedStartDate", header: t("Planned Start Date"), type: "string" },
        { id: "convertedPlannedEndDate", header: t("Planned End Date"), type: "string" },
        { id: "plannedDurationExport", header: t("Planned Duration"), type: "number" },
        { id: "remDurationExport", header: t("Remaining Duration"), type: "number" },
        { id: "progress", header: t("Progress"), type: "number" },
      ],
      date_format: "mm/dd/yyyy h:mm AM/PM",
    });
    setExportButtonAnchorEl(null);
  };

  const exportButtonTemplate = () => (
    <>
      <ButtonComponent
        onClick={(event) => setExportButtonAnchorEl(event.currentTarget)}
        buttonType={BUTTON_TYPES.TEXT_BUTTON}
        disabled={isGrouped || (contractId ? !contractCanDownload : !canDownload)}
      >
        <Text className="flex gap-2 items-center font-semibold">
          <ExportIcon className="h-4 w-4" />
          {t("Export")}
          <DownArrow />
        </Text>
      </ButtonComponent>
      <MenuComponent
        anchorEl={exportButtonAnchorEl}
        open={exportButtonOpen}
        onClose={() => setExportButtonAnchorEl(null)}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItemComponent
          onClick={() => {
            setupGanttExportForm(exportType.P6);
            gantt.exportToPrimaveraP6({
              server,
              skip_circular_links: false,
              tasks: {
                ActualFinish: function (task: Task) {
                  return task.actualEndDate;
                },
              },
            });
            setExportButtonAnchorEl(null);
          }}
        >
          <P6Icon />
          <Text>{t("Primavera P6")}</Text>
        </MenuItemComponent>
        <MenuItemComponent
          onClick={() => {
            setupGanttExportForm(exportType.MSP);
            gantt.exportToMSProject({ server });
            setExportButtonAnchorEl(null);
          }}
        >
          <MppIcon />
          <Text>{t("Microsoft Project")}</Text>
        </MenuItemComponent>
        <MenuItemComponent onClick={handleExcelExport}>
          <XlsxIcon />
          <Text>{t("Excel")}</Text>
        </MenuItemComponent>
      </MenuComponent>
    </>
  );

  const iconsTemplate = () => (
    <>
      {icons.map(({ Icon, onClick, label, disabled, hidden }) => (
        <>
          {!hidden && (
            <ToolTipComponent key={label} arrow content={label}>
              <IconButton
                onClick={onClick}
                disabled={disabled}
                sx={{
                  width: "3rem",
                  height: "2rem",
                  borderRadius: "0.5rem",
                  padding: 0,
                }}
                className={disabled ? "opacity-40 cursor-not-allowed" : ""}
              >
                <Icon style={{ fontSize: "1rem", color: "black" }} />
              </IconButton>
            </ToolTipComponent>
          )}
        </>
      ))}
    </>
  );

  const handleDeleteClick = async () => {
    const selectedId = gantt.getSelectedId();
    if (!selectedId) return;
    setDeleteInProgress(true);
    isDeletingTask.current = true;
    gantt.deleteTask(selectedId);
    isDeletingTask.current = false;

    try {
      const result = await axiosWrapper.post<GetScheduleActivitiesResponse>(deleteScheduleActivityUrl, {
        params: { projectId, scheduleId, spaceId },
        entity: "task",
        action: "delete",
        data: { id: selectedId },
      });

      if (result.data.status) {
        setCurrentSelectedTask({});
        setShowDeleteActivityModal(false);
        dispatch(
          showToastNotification({
            content: t("Activity Deleted Successfully."),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(result.data.errorMessage || SOMETHING_WENT_WRONG),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } catch {
      dispatch(
        showToastNotification({
          content: SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    } finally {
      setDeleteInProgress(false);
    }
  };

  const handleExpandAllClick = () => {
    gantt.batchUpdate(function () {
      gantt.eachTask(function (task: Task) {
        gantt.open(task.id);
      });
    });
  };
  const handleCollapseAllClick = () => {
    gantt.batchUpdate(function () {
      gantt.eachTask(function (task: Task) {
        gantt.close(task.id);
      });
    });
  };

  const handleZoomButtons = (level: number) => {
    if (level == 0) {
      setCurrentZoomLevel("min");
    } else if (level == zoomConfig.current.levels.length - 1) {
      setCurrentZoomLevel("max");
    } else {
      setCurrentZoomLevel("middle");
    }
  };

  function setZoom(level: number) {
    const zoomLevel = zoomConfig.current.levels[level];
    if (!zoomLevel) {
      return;
    }
    gantt.config.scale_unit = zoomLevel.scale_unit;
    gantt.config.step = zoomLevel.step;
    gantt.config.date_scale = zoomLevel.date_scale;
    gantt.config.subscales = zoomLevel.subscales;
    gantt.render(); // Re-render the Gantt chart with the new zoom level
    handleZoomButtons(level);
  }

  function zoomIn() {
    if (zoomConfig.current.currentLevel > 0) {
      zoomConfig.current.currentLevel -= 1;
      setZoom(zoomConfig.current.currentLevel);
    }
  }

  function zoomOut() {
    if (zoomConfig.current.currentLevel < zoomConfig.current.levels.length - 1) {
      zoomConfig.current.currentLevel += 1;
      setZoom(zoomConfig.current.currentLevel);
    }
  }

  const zoomToFit = () => {
    const tasks: Task[] = gantt.getTaskByTime();
    if (!tasks.length) return;

    // Guarded by the length check above; scheduled tasks always carry both dates. Erased assertions.
    let minDate = tasks[0]!.start_date!;
    let maxDate = tasks[0]!.end_date!;

    for (const task of tasks) {
      if (task.start_date! < minDate) minDate = task.start_date!;
      if (task.end_date! > maxDate) maxDate = task.end_date!;
    }

    // Date arithmetic: JS coerces both operands to epoch ms. Erased assertions.
    const diffInDays = ((maxDate as Date & number) - (minDate as Date & number)) / (1000 * 60 * 60 * 24);
    const diffInYears = diffInDays / 365;

    let newLevel = 0;
    if (diffInYears >= 1) newLevel = 1;
    else if (diffInYears > 10) newLevel = 2;

    zoomConfig.current.currentLevel = newLevel;
    setZoom(newLevel);
  };

  const handleIndentClick = async () => {
    const taskId = gantt.getSelectedId();
    const task = gantt.getTask(taskId);
    const previousTaskId = gantt.getPrevSibling(taskId);

    if (previousTaskId) {
      const oldParent = gantt.getParent(taskId).toString();
      const previousTask = gantt.getTask(previousTaskId);
      task.parent = previousTask.id;
      setSkipAfterTaskUpdate(true);
      gantt.updateTask(task.id);
      setSkipAfterTaskUpdate(false);

      const newParent = gantt.getParent(taskId).toString();

      const updates: GanttUpdateItem[] = [...refreshParentSummaries(newParent)];
      if (oldParent && oldParent !== "0" && String(oldParent) !== String(newParent)) {
        updates.push(...refreshParentSummaries(oldParent));
      }
      applyParentTypeUpdates(newParent, oldParent, updates);

      gantt.render();
      await saveBatchSummaryUpdates(updates);
    }
  };

  const handleOutdentClick = async () => {
    const taskId = gantt.getSelectedId();
    const task: Task = gantt.getTask(taskId);

    if (task.parent) {
      const oldParent = gantt.getParent(taskId).toString();
      const parentTask = gantt.getTask(task.parent);

      if (parentTask?.parent) {
        task.parent = parentTask.parent;
      } else {
        task.parent = 0;
      }

      setSkipAfterTaskUpdate(true);
      gantt.updateTask(task.id);
      setSkipAfterTaskUpdate(false);

      const newParent = gantt.getParent(taskId).toString();

      const updates: GanttUpdateItem[] = [...refreshParentSummaries(oldParent)];
      if (newParent && newParent !== "0" && String(oldParent) !== String(newParent)) {
        updates.push(...refreshParentSummaries(newParent));
      }
      applyParentTypeUpdates(newParent, oldParent, updates);

      gantt.render();
      await saveBatchSummaryUpdates(updates);
    }
  };

  const handleScheduleClick = async () => {
    setShowShimmer(true);
    const allTasks = gantt.getTaskByTime();
    const currentDate = new Date();
    const dateWith8AM = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 8, 0, 0, 0);
    const statusDate = GanttDetails.statusDate ? new Date(GanttDetails.statusDate) : dateWith8AM;

    for (const task of allTasks) {
      if (task.actualStartDate && task.actualEndDate) {
        task.start_date = task.actualStartDate;
        task.end_date = task.actualEndDate;
        task.duration = gantt.calculateDuration(task as Task & { start_date: Date; end_date: Date });
        task.progress = 1;
      } else if (task.actualStartDate && !task.actualEndDate) {
        task.start_date = task.actualStartDate;
        task.end_date = gantt.calculateEndDate({
          start_date: statusDate,
          duration: (1 - task.progress!) * task.plannedDuration,
          task: task,
        });
        task.duration = gantt.calculateDuration(task as Task & { start_date: Date; end_date: Date });
        task.progress = 1;
      } else if (
        !task.actualStartDate &&
        !task.actualEndDate &&
        task.start_date &&
        task.start_date.getTime() <= statusDate.getTime()
      ) {
        if (taskIdAndPredecessorMapper.current[task.id]) {
          let allPredHaveActualEndDate = true;

          // Guarded by the `if (taskIdAndPredecessorMapper.current[task.id])` check above; every
          // predecessor entry carries its `predId`. Erased assertions.
          for (const pred of taskIdAndPredecessorMapper.current[task.id]!) {
            const predTask = gantt.getTask(pred.predId!);
            if (!predTask.actualEndDate) {
              allPredHaveActualEndDate = false;
              break;
            }
          }

          if (allPredHaveActualEndDate) {
            task.start_date = statusDate;
            task.end_date = gantt.calculateEndDate({
              start_date: statusDate,
              duration: task.plannedDuration,
              task: task,
            });
            task.duration = gantt.calculateDuration(task as Task & { start_date: Date; end_date: Date });
            task.progress = 1;
          }
        } else {
          task.start_date = statusDate;
          task.end_date = gantt.calculateEndDate({
            start_date: statusDate,
            duration: task.plannedDuration,
            task: task,
          });
          task.duration = gantt.calculateDuration(task as Task & { start_date: Date; end_date: Date });
          task.progress = 1;
        }
      }
    }

    gantt.render();

    gantt.config.auto_scheduling = true;
    gantt.config.auto_scheduling_strict = true;
    gantt.config.auto_scheduling_compatibility = true;
    gantt.config.auto_scheduling_use_progress = true;
    gantt.autoSchedule();
  };

  const showWarningModal = (source: string) => {
    setShowWarning({ status: true, source });
  };

  const separator = {
    name: "separator",
    template: () => <div className="border-s border-s-[#000109] self-stretch h-8"></div>,
  };

  const icons = [
    { Icon: ExpandAll, onClick: handleExpandAllClick, label: t("Expand All"), disabled: false, hidden: false },
    { Icon: CollapseAll, onClick: handleCollapseAllClick, label: t("Collapse All"), disabled: false, hidden: false },
    { Icon: ZoomIn, onClick: zoomIn, label: t("Zoom In"), disabled: currentZoomLevel == "min", hidden: false },
    { Icon: ZoomOut, onClick: zoomOut, label: t("Zoom Out"), disabled: currentZoomLevel == "max", hidden: false },
    { Icon: ZoomToFit, onClick: zoomToFit, label: t("Zoom To Fit"), disabled: false, hidden: false },
    {
      Icon: FormatIndentIncreaseIcon,
      onClick: handleIndentClick,
      label: t("Indent"),
      disabled: !currentSelectedTask.id || !canEdit || isGrouped,
      hidden: contractId || scheduleVersionId,
    },
    {
      Icon: FormatIndentDecreaseIcon,
      onClick: handleOutdentClick,
      label: t("Outdent"),
      disabled: !currentSelectedTask.id || !canEdit || isGrouped,
      hidden: contractId || scheduleVersionId,
    },
  ];

  const deleteActivityFooterContent = () => (
    <div className="flex gap-4 px-6 pb-6">
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        loading={deleteInProgress}
        onClick={() => {
          handleDeleteClick();
        }}
      >
        <Text>{t("Confirm")}</Text>
      </ButtonComponent>

      <ButtonComponent
        buttonType={BUTTON_TYPES.BORDER_BUTTON}
        disabled={deleteInProgress}
        onClick={() => {
          setShowDeleteActivityModal(false);
        }}
      >
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  const deleteActivityModalContent = () => (
    <>
      <div
        style={{
          borderBottomColor: "#ECF3FE",
          borderBottomWidth: "0.0625rem",
          marginInlineStart: "1.5rem",
          marginInlineEnd: "1.5rem",
        }}
      ></div>
      <div className="flex flex-col gap-6 p-6">
        <Text className="font-normal text-sm ">{t("Are you sure you want to delete the selected Activity?")}</Text>
        <div className="bg-yellow-100 p-4 rounded-lg">
          <Text className="font-normal text-xs whitespace-normal break-words">
            {t(
              "Deleting this Activity will remove all this Activity’s assignments regarding Predecessors, Successors and Resources.",
            )}
          </Text>
        </div>
      </div>
    </>
  );

  const deleteActivityModal = () => {
    return (
      showDeleteActivityModal && (
        <ModalStateDialogComponent
          modalPadding={0}
          onCancel={() => {
            setShowDeleteActivityModal(false);
          }}
          headerTitle={
            <div className="w-full h-16 pt-6 ps-6">{<Text className="h-[29]">{t("Delete Activity")}</Text>} </div>
          }
          modelContent={deleteActivityModalContent()}
          footerContent={deleteActivityFooterContent()}
          isVisible={showDeleteActivityModal}
          width="27.5rem"
        />
      )
    );
  };

  const handleCancelClick = () => {
    setFileSoftware("");
    setShowImportModal(false);
    setShowUpdateModal(false);
    setFile(null);
    setUpdateFile(null);
    setBaselineFile(null);
    setImportBaseline(false);
    setImportTasksInProgress(false);
    setUpdateTasksInProgress(false);
  };

  // `baseline` is passed the baseline File itself from one call site and left defaulted at the
  // others, so it is a truthiness flag rather than a boolean.
  const handleFileCallback = async (data: ImportProjectResult, baseline: File | null | boolean = false) => {
    if (baseline) {
      const P6Params: importParamsWithCallback = {
        server: `${getBackendUrl()}/schedule/importScheduleTasksNew?scheduleId=${scheduleId}&importType=baseline`,
        data: baselineFile,
        taskProperties: [...customImportFields, "WBS", "ActivityID"],
        callback: (project: ImportProjectResult) => handleFileCallback(project),
      };
      gantt.importFromPrimaveraP6(P6Params);
    } else {
      if (data?.status) {
        if (data.worktimes) {
          dispatch(setScheduleDetails({ ...GanttDetails, worktimes: data.worktimes }));
        }
        setRerenderGantt(true);
      } else {
        dispatch(
          showToastNotification({
            content: baselineFile ? t("Attached XER's are not correct") : SOMETHING_WENT_WRONG,
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
      handleCancelClick();
    }
  };

  const handleFileupload = async () => {
    let importType, data;
    if (file) {
      setImportTasksInProgress(true);
      importType = "file";
      data = file;
    } else if (updateFile) {
      setUpdateTasksInProgress(true);
      importType = "update";
      data = updateFile;
    }
    const server = `${getBackendUrl()}/schedule/importScheduleTasksNew?scheduleId=${scheduleId}&importType=${importType}`;
    if (file || updateFile) {
      const P6Params: importParamsWithCallback = {
        server,
        data,
        taskProperties: [...customImportFields, "WBS", "ActivityID"],
        callback: (project: ImportProjectResult) => handleFileCallback(project, baselineFile),
      };
      const MSPParams: importParamsWithCallback = {
        server,
        data,
        taskProperties: customImportFields,
        callback: (project: ImportProjectResult) => handleFileCallback(project),
      };
      if (fileSoftware == P6) {
        gantt.importFromPrimaveraP6(P6Params);
      } else if (fileSoftware == MSP) {
        gantt.importFromMSProject(MSPParams);
      }
    }
  };

  const importFooterContent = () => (
    <div className="flex gap-4">
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={handleFileupload}
        loading={importTasksInProgress}
        disabled={!file || (importBaseline && !baselineFile)}
      >
        <Text>{t("Import")}</Text>
      </ButtonComponent>

      <ButtonComponent
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        disabled={importTasksInProgress}
        onClick={handleCancelClick}
      >
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  const updateFooterContent = () => (
    <div className="flex gap-4">
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={handleFileupload}
        loading={updateTasksInProgress}
        disabled={!updateFile}
      >
        <Text>{t("Update")}</Text>
      </ButtonComponent>

      <ButtonComponent
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        disabled={updateTasksInProgress}
        onClick={handleCancelClick}
      >
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  const importModalContent = () => (
    <div className="flex flex-col gap-6">
      <div className="flex w-full items-center gap-4">
        <input
          ref={fileInputRef}
          type="file"
          accept={SupportedExtension}
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) {
              setFile(file);
              const ext = file.name.split(".").pop();
              if (ext === XER) setFileSoftware(P6);
              else if (ext === MPP) setFileSoftware(MSP);
            }
          }}
        />

        <ButtonComponent
          className={file ? "bg-[var(--button-light)] text-[var(--disabled-text-gray)]" : ""}
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
          onClick={() => fileInputRef.current!.click()}
        >
          <Text>{t("Upload File ({{extensions}} supported)", { extensions: SupportedExtension })}</Text>
        </ButtonComponent>

        <Text className="text-sm">{file ? file.name : t("No File Chosen")}</Text>
      </div>
      {fileSoftware == P6 && (
        <div className="flex w-full items-center gap-4">
          <Text className="flex w-auto text-base font-semibold items-center gap-1">
            {t("Import baseline file")}
            <IconComponent
              name="details"
              toolTipContent={t("The baseline will be imported as a separate XER file")}
              position="bottom"
              width="1rem"
              height="1rem"
            />
          </Text>
          <RadioButton onChange={() => setImportBaseline(!importBaseline)} checked={importBaseline} label={t("Yes")} />
          <RadioButton
            onChange={() => {
              setImportBaseline(!importBaseline);
              setBaselineFile(null);
            }}
            checked={!importBaseline}
            label={t("No")}
          />
        </div>
      )}

      {fileSoftware == P6 && importBaseline && (
        <div className="flex w-full items-center gap-4">
          <input
            ref={BaselineInputRef}
            type="file"
            accept={`.${XER}`}
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                setBaselineFile(file);
              }
            }}
          />
          <ButtonComponent
            className={baselineFile ? "bg-[var(--button-light)] text-[var(--disabled-text-gray)]" : ""}
            buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
            onClick={() => BaselineInputRef.current!.click()}
          >
            <Text>{t("Upload File (.xer supported)")}</Text>
          </ButtonComponent>

          <Text className="text-sm">{baselineFile ? baselineFile.name : t("No File Chosen")}</Text>
        </div>
      )}

      {fileSoftware == P6 && importBaseline && baselineFile && (
        <div className="bg-yellow-100 p-4 rounded-lg">
          <Text className="font-normal text-xs whitespace-normal break-words">
            {t(
              "Imported Baseline will update the Planned Start, Planned End and Planned Duration Column. All the calculations related to the same will be updated accordingly.",
            )}
          </Text>
        </div>
      )}
    </div>
  );

  const updateModalContent = () => (
    <div className="flex flex-col gap-6">
      <div className="flex w-full items-center gap-4">
        <input
          ref={updateFileInputRef}
          type="file"
          accept={SupportedExtension}
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) {
              setUpdateFile(file);
              const ext = file.name.split(".").pop();
              if (ext === XER) setFileSoftware(P6);
              else if (ext === MPP) setFileSoftware(MSP);
            }
          }}
        />

        <ButtonComponent
          className={file ? "bg-[var(--button-light)] text-[var(--disabled-text-gray)]" : ""}
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
          onClick={() => updateFileInputRef.current!.click()}
        >
          <Text>{t("Upload File ({{extensions}} supported)", { extensions: SupportedExtension })}</Text>
        </ButtonComponent>

        <Text className="text-sm">{updateFile ? updateFile.name : t("No File Chosen")}</Text>
      </div>
      {updateFile && (
        <div className="flex flex-col gap-3 border border-stroke_primary_light p-3 rounded-lg">
          <Text className="text-sm font-semibold">{t("Add")}</Text>
          <Text className="font-normal text-xs whitespace-normal break-words">
            {t("No new Activities will be added.")}
          </Text>
        </div>
      )}
      {updateFile && (
        <div className="flex flex-col gap-3 border border-stroke_primary_light p-3 rounded-lg">
          <Text className="text-sm font-semibold">{t("Remove")}</Text>
          <Text className="font-normal text-xs whitespace-normal break-words">
            {t("Existing Activities will not be removed, even if they do not exist in the uploaded file.")}
          </Text>
        </div>
      )}
      {updateFile && (
        <div className="flex flex-col gap-3 border border-stroke_primary_light p-3 rounded-lg">
          <Text className="text-sm font-semibold">{t("Update")}</Text>
          <Text className="font-normal text-xs whitespace-normal break-words">
            {t(
              "Data in the following columns will be updated for Activities that already exist in the Schedule: Start Date, End Date, Duration, Planned Start, Planned End, Planned Duration, Actual Start Date, Actual End Date, and Progress Percentage.",
            )}
          </Text>
        </div>
      )}
    </div>
  );

  const importModal = () => {
    return (
      showImportModal && (
        <ModalStateDialogComponent
          allowModalPadding={false}
          headerTitle={t("Import")}
          onCancel={() => {
            setShowImportModal(false);
            setFile(null);
            setImportTasksInProgress(false);
          }}
          modelContent={importModalContent()}
          footerContent={importFooterContent()}
          isVisible={showImportModal}
          width="37.5rem"
        />
      )
    );
  };

  const updateModal = () => {
    return (
      <ModalStateDialogComponent
        headerTitle={t("Update Schedule")}
        onCancel={() => {
          setShowUpdateModal(false);
          setUpdateFile(null);
          setUpdateTasksInProgress(false);
        }}
        modelContent={updateModalContent()}
        footerContent={updateFooterContent()}
        isVisible={showUpdateModal}
        width="37.5rem"
      />
    );
  };

  const ganttToolbar = [
    {
      name: "New",
      id: "new",
      template: newButtonTemplate,
      hidden: !!contractId || scheduleVersionId,
      disabled: isGrouped || !canEdit,
    },
    {
      name: t("Edit"),
      id: "edit",
      prefixIconTemplate: <EditIcon className="w-4 h-4" />,
      disabled: isGrouped || !currentSelectedTask.id || !canEdit,
      cssClass: "font-semibold text-sm",
      buttonType: BUTTON_TYPES.TEXT_BUTTON,
      hidden: contractId || inlineEditingEnabled || scheduleVersionId,
    },
    {
      name: t("Update"),
      id: "update",
      prefixIconTemplate: <UpdateIcon className="w-4 h-4" />,
      cssClass: "font-semibold text-sm",
      buttonType: BUTTON_TYPES.TEXT_BUTTON,
      hidden: contractId || !inlineEditingEnabled || scheduleVersionId,
      disabled: isGrouped || !canEdit,
    },
    {
      name: t("Cancel"),
      id: "cancel",
      prefixIconTemplate: <CancelIcon className="w-4 h-4" />,
      cssClass: "font-semibold text-sm",
      buttonType: BUTTON_TYPES.TEXT_BUTTON,
      hidden: contractId || !inlineEditingEnabled || scheduleVersionId,
      disabled: isGrouped,
    },
    {
      name: t("Progress"),
      id: "progress",
      prefixIconTemplate: <ProgressUpdates className="w-4 h-4" />,
      disabled:
        isGrouped ||
        (contractId && !getContractCRUDPermission(contractDetails, changeOrderState)) ||
        !currentSelectedTask.id ||
        !canEdit,
      cssClass: "font-semibold text-sm",
      buttonType: BUTTON_TYPES.TEXT_BUTTON,
      hidden: contractId || inlineEditingEnabled || scheduleVersionId,
    },
    {
      name: t("Delete"),
      id: "delete",
      prefixIconTemplate: <DeleteIcon className="w-4 h-4" />,
      disabled: isGrouped || !currentSelectedTask.id || !canEdit,
      cssClass: "font-semibold text-sm",
      buttonType: BUTTON_TYPES.TEXT_BUTTON,
      hidden: contractId || inlineEditingEnabled || scheduleVersionId,
    },
    {
      name: t("Attach Activities"),
      id: "attachTasks",
      disabled: isGrouped || !getContractCRUDPermission(contractDetails, changeOrderState),
      cssClass: "font-semibold text-sm",
      buttonType: BUTTON_TYPES.TEXT_BUTTON,
      hidden: !contractId,
    },
    {
      name: t("Un-Link"),
      id: "unLinkTask",
      disabled: isGrouped || !getContractCRUDPermission(contractDetails, changeOrderState) || !currentSelectedTask.id,
      cssClass: "font-semibold text-sm",
      buttonType: BUTTON_TYPES.TEXT_BUTTON,
      hidden: !contractId,
    },
    {
      name: t("Link BOQ Items"),
      id: "linkBoqItems",
      cssClass: "font-semibold text-sm",
      buttonType: BUTTON_TYPES.TEXT_BUTTON,
      hidden: !contractId,
      disabled: isGrouped || !getContractCRUDPermission(contractDetails, changeOrderState) || !currentSelectedTask.id,
    },
    {
      name: t("Update"),
      id: "updateTasksinContract",
      disabled: isGrouped || !getContractCRUDPermission(contractDetails, changeOrderState) || !currentSelectedTask.id,
      cssClass: "font-semibold text-sm",
      buttonType: BUTTON_TYPES.TEXT_BUTTON,
      hidden: !contractId,
    },
    separator,
    {
      id: "iconTemplate",
      template: iconsTemplate,
    },
    separator,
    {
      name: t("Export"),
      id: "export",
      template: exportButtonTemplate,
      disabled: isGrouped || (contractId ? !contractCanDownload : !canDownload),
    },
    {
      name: t("Schedule Now"),
      id: "schedule",
      prefixIconTemplate: <ScheduleIcon className="w-4 h-4" />,
      cssClass: "font-semibold text-sm",
      hidden: scheduleVersionId,
      disabled: isGrouped || (contractId ? true : !canEdit),
      buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
    },
  ];

  const toolbarClickHandler = (args: string) => {
    if (args == "edit") {
      setShowActivityInformationModal(true);
    } else if (args == "update") {
      gantt.ext.inlineEditors.save();
    } else if (args == "cancel") {
      gantt.ext.inlineEditors.hide();
    } else if (args == "progress") {
      const selectedId = gantt.getSelectedId();
      if (selectedId && gantt.hasChild(selectedId)) {
        dispatch(
          showToastNotification({
            content: t(
              "Progress cannot be updated on summary activities. It is automatically calculated from child activities.",
            ),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return;
      }
      setShowProgressModal(true);
    } else if (args == "delete") {
      setShowDeleteActivityModal(true);
    } else if (args == "schedule") {
      handleScheduleClick();
    } else if (args == "attachTasks") {
      attachTaskModalState("Attach", true);
    } else if (args == "unLinkTask") {
      showWarningModal("unLinkTask");
    } else if (args == "linkBoqItems") {
      setShowBoqModal(true);
    } else if (args == "updateTasksinContract") {
      attachTaskModalState("Update", true);
    }
  };

  return { ganttToolbar, toolbarClickHandler, deleteActivityModal, importModal, updateModal };
};
export default useToolBarInGantt;
